import { FC } from 'react';
import { IModal, useUuiContext } from '@epam/uui-core';
import { Panel, IconButton, Text, FlexRow } from '@epam/promo';
import { ReactComponent as ActionDeleteFillIcon } from '@epam/assets/icons/action-delete-fill.svg';
import { ReactComponent as ContentEditFillIcon } from '@epam/assets/icons/content-edit-fill.svg';
import { ReactComponent as ActionAddFillIcon } from '@epam/assets/icons/action-add-fill.svg';
import { RoleLabel } from './RoleLabel';
import { UserGroupEditModal } from '../UserGroupEditModal/UserGroupEditModal';
import { ModalBody } from 'components/modals/ModalBody';
import { MODAL_ACTION_TYPE } from 'components/modals/BaseModal';
import ConfirmBotIcon from 'assets/icons/confirm-modal-bot.svg';
import css from './UserGroup.module.scss';
import { useUserGroupUpdateNameQuery } from 'hooks/api/userGroups/useUserGroupUpdateNameQuery';
import {
  useCreateUserRolesForAGroup,
  useUserGroupDeleteQuery,
} from 'hooks/api/userGroups';
import { useModal } from 'hooks';

interface IUserGroup {
  id: string;
  name: string;
  roles: Array<{
    id: string;
    name: string;
  }>;
}

export const UserGroup: FC<IUserGroup> = ({ id, name, roles }: IUserGroup) => {
  const modal = useModal();
  const { uuiModals } = useUuiContext();
  const { mutate: updateUserGroupName } = useUserGroupUpdateNameQuery();
  const { mutate: createUserRoles } = useCreateUserRolesForAGroup();
  const { mutate: deleteUserGroup } = useUserGroupDeleteQuery();

  const onConfirmUpdateGroupName = (newName: string) => {
    updateUserGroupName({
      userGroupId: id,
      newName,
    });
  };

  const onConfirmAddGroupRoles = (newRolesList: string) => {
    createUserRoles({
      userGroupId: id,
      rolesList: newRolesList.split(','),
    });
  };

  const handleAddRoles = () => {
    uuiModals
      .show<void>((modalProps: IModal<void>) => (
        <UserGroupEditModal
          showRulesHint
          confirmCaption="Add Users Role(s)"
          helperText="Role name"
          modalProps={modalProps}
          placeholder="Enter roles names"
          title="Add Roles"
          onConfirm={onConfirmAddGroupRoles}
        />
      ))
      .catch((e) => console.error(e));
  };

  const handleEditGroup = () => {
    uuiModals
      .show<void>((modalProps: IModal<void>) => (
        <UserGroupEditModal
          confirmCaption="Update Group Name"
          helperText="Group name"
          initialValue={name}
          modalProps={modalProps}
          placeholder="Enter group name"
          title={`Edit '${name}'`}
          onConfirm={onConfirmUpdateGroupName}
        />
      ))
      .catch((e) => console.error(e));
  };

  return (
    <Panel shadow>
      <FlexRow cx={css.header}>
        <Text fontSize={16} fontWeight="600">
          {name.toUpperCase()}
        </Text>
        <FlexRow columnGap={4}>
          <IconButton icon={ActionAddFillIcon} onClick={handleAddRoles} />
          <IconButton icon={ContentEditFillIcon} onClick={handleEditGroup} />
          <IconButton
            icon={ActionDeleteFillIcon}
            onClick={() => {
              if (roles.length > 0) {
                modal.showdata({
                  data: { title: 'Cannot Delete Group' },
                  height: '300',
                  children: (
                    <ModalBody
                      bot={MODAL_ACTION_TYPE.CONFIRM}
                      botImg={ConfirmBotIcon}
                      text={`Remove all roles from '${name}' before deleting the group.`}
                    />
                  ),
                });
              } else {
                modal.confirm({
                  text: `Are you sure you want to delete '${name}' group?`,
                  onSuccess: () => deleteUserGroup(id),
                });
              }
            }}
          />
        </FlexRow>
      </FlexRow>
      <FlexRow cx={css.rolesContainer}>
        {roles.map((role) => (
          <RoleLabel key={role.id} id={role.id} name={role.name} />
        ))}
      </FlexRow>
    </Panel>
  );
};
